import { EntityType } from '@repo/common/enums/entityType';
import { Icon } from '@repo/foundations';
import type { MouseEvent, ReactNode, Ref } from 'react';
import { forwardRef, useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useAuthUser, type ITemplate } from '../../api';
import { useCurrentTeamId } from '../../utils/hook/useCurrentTeamId.ts';
import { entityModalStore } from '../EntityModal/store.tsx';
import { TemplatesButton } from './TemplatesButton';

interface TemplatesButtonGroupProps {
	type: EntityType;
	withForm?: boolean;
	withSetDefault?: boolean;
	handleNew: (ev: MouseEvent<HTMLButtonElement>) => void;
	leftButtonTooltip?: ReactNode;
	label: string;
}

function TemplatesButtonGroupInternal(
	{
		type,
		withForm,
		withSetDefault,
		handleNew,
		leftButtonTooltip,
		label,
	}: TemplatesButtonGroupProps,
	ref: Ref<HTMLButtonElement>
) {
	const { user } = useAuthUser();
	const teamId = useCurrentTeamId();
	const navigate = useNavigate();
	const handleUseTemplate = useCallback(
		async (template: ITemplate) => {
			// For document type, we don't want to open the modal
			if (type === EntityType.document) {
				// Set the template and its content
				await entityModalStore.setTemplate(template, type, teamId, user.id);
				await entityModalStore.createDocument(navigate, true);
				return;
			}

			// For other entity types, open the modal as usual
			entityModalStore.open({
				type,
				teamId,
				template,
				metadata: {
					owners: [user.id],
				},
			});
		},
		[teamId, type, user, navigate]
	);

	return (
		<TemplatesButton
			splitButtonProps={{
				onClick: handleNew,
				variant: 'primary',
				label: label,
				leftButtonTooltip: leftButtonTooltip,
				ref: ref,
			}}
			type={type}
			handleUseTemplate={handleUseTemplate}
			useConfirmation={false}
		>
			<Icon name="chevronDown" />
		</TemplatesButton>
	);
}

export const TemplatesButtonGroup = forwardRef(TemplatesButtonGroupInternal);
